import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const CharacterContext = createContext(null);

export const CharacterProvider = ({ username, children }) => {
  const location = useLocation();
  const [character, setCharacter] = useState(null);
  const [hasCharacter, setHasCharacter] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [depotItemCount, setRawDepotItemCount] = useState(0);
  // Depo sayısı artınca DepotNotice bildirimi gösterir. Taban değer bilinmeden (null) bildirim yok.
  const [depotNotice, setDepotNotice] = useState(null);
  const depotBaselineRef = useRef(null);
  const setDepotItemCount = useCallback((value) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return;
    const prev = depotBaselineRef.current;
    depotBaselineRef.current = value;
    setRawDepotItemCount(value);
    if (prev !== null && value > prev) {
      setDepotNotice({ id: Date.now() + Math.random(), added: value - prev });
    }
  }, []);
  const inFlightRef = useRef(null);
  const characterRef = useRef(null);
  characterRef.current = character;

  const refreshDepotCount = useCallback(async () => {
    if (!username) {
      depotBaselineRef.current = null;
      setRawDepotItemCount(0);
      return 0;
    }
    try {
      const response = await axios.get(`/api/inventory/pending?username=${username}`);
      const count = response.data?.success ? (response.data.items || []).length : 0;
      // Sunucudaki gerçek sayı = taban değer (bildirim göstermeden)
      depotBaselineRef.current = count;
      setRawDepotItemCount(count);
      return count;
    } catch {
      return 0;
    }
  }, [username]);

  const refreshCharacterMeta = useCallback(async () => {
    if (!username) return null;
    try {
      const response = await axios.get(
        `/api/user-character/exp-progress?username=${encodeURIComponent(username)}`,
        { timeout: 10000 }
      );
      const data = response.data;
      if (!data?.success) return null;
      setCharacter((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          level: data.level ?? prev.level,
          exp: data.exp ?? prev.exp,
          gold: data.gold ?? prev.gold,
          statPoints: data.statPoints ?? prev.statPoints,
          skillPoints: data.skillPoints ?? prev.skillPoints,
          currentLevelExp: data.currentLevelExp ?? prev.currentLevelExp,
          expToNextLevel: data.expToNextLevel ?? prev.expToNextLevel,
          expProgress: data.expProgress ?? prev.expProgress,
          maxHP: data.maxHp ?? prev.maxHP,
          maxMP: data.maxMp ?? prev.maxMP,
        };
      });
      return data;
    } catch {
      return null;
    }
  }, [username]);

  const refreshCharacter = useCallback(async (opts = {}) => {
    if (!username) {
      setCharacter(null);
      setHasCharacter(false);
      setInitialLoadDone(true);
      return null;
    }

    const {
      syncCombat = false,
      refillMp = false,
      resetHp = false,
    } = opts;

    if (inFlightRef.current) {
      return inFlightRef.current;
    }

    const params = new URLSearchParams({ username });
    if (syncCombat) params.set('syncCombat', '1');
    if (refillMp) params.set('refillMp', '1');
    if (resetHp) params.set('resetHp', '1');

    const promise = axios
      .get(`/api/user-character?${params.toString()}`, { timeout: 20000 })
      .then((response) => {
        const data = response.data;
        if (data?.success && data.hasCharacter) {
          setCharacter(data.character);
          setHasCharacter(true);
          return data.character;
        }
        if (data?.success && data.hasCharacter === false) {
          setCharacter(null);
          setHasCharacter(false);
          return null;
        }
        return null;
      })
      .catch((err) => {
        console.error('Error fetching character:', err);
        throw err;
      })
      .finally(() => {
        inFlightRef.current = null;
        setInitialLoadDone(true);
      });

    inFlightRef.current = promise;
    return promise;
  }, [username]);

  // Only block UI before the first character fetch completes; later refreshes are silent.
  const loading = Boolean(username) && !initialLoadDone;

  useEffect(() => {
    if (!username) {
      setCharacter(null);
      setHasCharacter(false);
      setInitialLoadDone(true);
      return;
    }

    const onBattle = location.pathname === '/battle';
    if (!onBattle && !characterRef.current) {
      setInitialLoadDone(false);
    }

    refreshCharacter().catch(() => {});

    if (onBattle) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      refreshCharacterMeta().catch(() => {});
    }, 15000);

    return () => {
      clearInterval(intervalId);
    };
  }, [username, refreshCharacter, refreshCharacterMeta, location.pathname]);

  // Açılışta depodaki gerçek sayıyı taban olarak al.
  useEffect(() => {
    refreshDepotCount().catch(() => {});
  }, [refreshDepotCount]);

  return (
    <CharacterContext.Provider
      value={{
        character,
        hasCharacter,
        loading,
        refreshCharacter,
        setCharacter,
        depotItemCount,
        setDepotItemCount,
        refreshDepotCount,
        depotNotice,
      }}
    >
      {children}
    </CharacterContext.Provider>
  );
};

export const useCharacter = () => {
  const context = useContext(CharacterContext);
  if (!context) {
    throw new Error('useCharacter must be used within CharacterProvider');
  }
  return context;
};

export const useCharacterOptional = () => useContext(CharacterContext);
